import { ReactNode, ReactElement, useState, forwardRef } from 'react';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import { useSpring, animated } from 'react-spring/web.cjs'; // web.cjs is required for IE 11 support
import styled from '@emotion/styled';
import { gray02, gray04, white } from 'presentations/views/styles/color';
import { borderRadius, spacing } from 'presentations/views/styles';

interface Props {
  children: ReactNode;
  isOpen: boolean;
  onChange?: any;
  className?: string;
  disablePortal?: boolean;
  hideBackdrop?: boolean;
  keepMounted?: boolean;
  disableAnimate?: boolean;
}

export default function Component(props: Props) {
  const handleClose = () => {
    if (props.onChange) {
      props.onChange(false);
    }
  };

  return (
    <StyledModal
      className={props.className}
      aria-labelledby="spring-modal-title"
      aria-describedby="spring-modal-description"
      open={props.isOpen}
      onClose={handleClose}
      closeAfterTransition
      hideBackdrop={props.hideBackdrop}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: props.disableAnimate ? 0 : 500,
        transitionDuration: props.disableAnimate ? 0 : 500,
      }}
      disablePortal={props.disablePortal}
      keepMounted={props.keepMounted}
    >
      <Container>
        {props.onChange && (
          <CloseIconButton onClick={handleClose}>
            <CloseIcon name="close" />
          </CloseIconButton>
        )}
        {props.disableAnimate ? (
          props.children
        ) : (
          <Fade in={props.isOpen}>
            <>{props.children}</>
          </Fade>
        )}
      </Container>
    </StyledModal>
  );
}

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface FadeProps {
  children?: ReactElement;
  in: boolean;
  onEnter?: () => {};
  onExited?: () => {};
}

const Fade = forwardRef<HTMLDivElement, FadeProps>(function Fade(props, ref) {
  const { in: open, children, onEnter, onExited, ...other } = props;
  const style = useSpring({
    from: { opacity: 0 },
    to: { opacity: open ? 1 : 0 },
    onStart: () => {
      if (open && onEnter) {
        onEnter();
      }
    },
    onRest: () => {
      if (!open && onExited) {
        onExited();
      }
    },
  });

  return (
    <animated.div ref={ref} style={style} {...other}>
      {children}
    </animated.div>
  );
});

const Container = styled.div`
  position: relative;
`;
const CloseIconButton = styled(IconButton)`
  position: absolute;
  right: 18px;
  top: 18px;
`;
const CloseIcon = styled(Icon)`
  color: ${gray02};
  width: 24px;
  height: 24px;
`;
